"use client";

import { type PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { type PackageDisplayPricing } from "@/lib/revenuecat/package-pricing";
import { Check } from "lucide-react";
import { PlanValueBanner } from "./PlanValueBanner";

const PERIOD_LABELS: Record<PackageType, string> = {
  annual: "per month",
  monthly: "per month",
  weekly: "per week",
};

const BILLING_COPY: Record<PackageType, string> = {
  annual: "One payment covers 12 months.",
  monthly: "Renews monthly. Cancel anytime.",
  weekly: "Renews weekly. Cancel anytime.",
};

const PLAN_DESCRIPTIONS: Record<PackageType, string> = {
  annual: "Full membership at the lowest monthly rate.",
  monthly: "Full membership with simple monthly billing.",
  weekly: "Full membership with maximum flexibility.",
};

function formatCurrencyAmount(amount: number, currencyCode: string) {
  try {
    return new Intl.NumberFormat("en", {
      currency: currencyCode,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: "currency",
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

export function ThreePlanMembershipCard({
  annualSavingsPercent,
  disabled = false,
  isLoading = false,
  onPurchase,
  packageType,
  pricing,
  showFreeTrial = false,
}: {
  annualSavingsPercent: number | null;
  disabled?: boolean;
  isLoading?: boolean;
  onPurchase: (packageType: PackageType) => void;
  packageType: PackageType;
  pricing: PackageDisplayPricing;
  showFreeTrial?: boolean;
}) {
  const pkg = REVENUECAT_CONFIG.packages[packageType];
  const isAnnual = packageType === "annual";
  const headlinePrice = isAnnual
    ? formatCurrencyAmount(pricing.priceAmount / 12, pricing.currencyCode)
    : pricing.priceFormatted;
  const planLabel =
    packageType === "annual"
      ? annualSavingsPercent === null
        ? "Best value"
        : `Best value · Save ${annualSavingsPercent}%`
      : pkg.badge || "AI speaking membership";

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/15 bg-[#15161B]">
      <PlanValueBanner packageType={packageType}>{planLabel}</PlanValueBanner>

      <div className="flex flex-1 flex-col p-6">
        <header>
          <h3 className="text-2xl font-bold text-white">{pkg.displayName}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[#AEAEB2]">
            {PLAN_DESCRIPTIONS[packageType]}
          </p>
        </header>

        <section className="mt-6 border-t border-white/10 pt-5">
          <div className="flex flex-wrap items-baseline gap-2">
            {!isAnnual && pricing.originalPriceFormatted && (
              <span className="text-lg font-semibold text-white/35 line-through">
                {pricing.originalPriceFormatted}
              </span>
            )}
            <span className="text-4xl font-bold text-white">{headlinePrice}</span>
            <span className="text-sm text-[#AEAEB2]">{PERIOD_LABELS[packageType]}</span>
          </div>

          {isAnnual && (
            <p className="mt-2 text-sm text-white/75">
              {pricing.priceFormatted} billed annually
              {pricing.originalPriceFormatted ? (
                <span className="ml-2 text-white/35 line-through">
                  {pricing.originalPriceFormatted}
                </span>
              ) : null}
            </p>
          )}

          <p className="mt-2 text-xs text-[#AEAEB2]">{BILLING_COPY[packageType]}</p>

          {(showFreeTrial || pricing.hasDiscountPrice) && (
            <div className="mt-3 space-y-1 text-xs font-semibold text-emerald-300">
              {showFreeTrial && <p>Free for 3 days</p>}
              {pricing.hasDiscountPrice && (
                <p>
                  Promotional pricing applied
                  {pricing.promotionalPeriod ? ` for ${pricing.promotionalPeriod}` : ""}
                </p>
              )}
            </div>
          )}
        </section>

        <ul className="my-6 space-y-3 border-t border-white/10 pt-5">
          {pkg.features.map((feature) => (
            <li className="flex items-start gap-2 text-sm text-white/85" key={feature}>
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FD954F]" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <button
          aria-label={
            showFreeTrial
              ? `Start 3-day ${pkg.displayName.toLowerCase()} trial`
              : `Start ${pkg.displayName.toLowerCase()} membership`
          }
          className="mt-auto min-h-12 w-full rounded-xl bg-[#FD954F] px-4 py-3 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14] disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled || isLoading}
          onClick={() => onPurchase(packageType)}
          type="button"
        >
          {isLoading
            ? "Preparing checkout…"
            : showFreeTrial
              ? "Start 3-day trial"
              : `Start ${pkg.displayName.toLowerCase()} membership`}
        </button>
      </div>
    </article>
  );
}
